/**
 * useSaveWorkflow Hook
 *
 * Provides workflow save functionality with React Query mutation.
 * Handles serialization, API call, and dirty state management.
 *
 * VALIDATION BEHAVIOR:
 * - Validates workflow against Azure API before saving
 * - Blocks save if validation fails
 * - Shows validation errors via notification system
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { telemetryService } from '@/services/telemetryService';
import { trackFunnelStep, FunnelNames } from '@/services/telemetryFunnels';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useAppStore } from '@/stores/appStore';
import { useAuth } from '@/auth/useAuth';
import { saveWorkflow } from '@/services/logicAppsApi';
import {
  validateWorkflowWithAzure,
  type ValidationResult,
} from '@/services/workflowValidationService';
import { useNotificationStore } from '@/stores/notificationStore';
import type { NotificationType } from '@/stores/notificationStore';
import { updateFoundryAgentByVersion } from '@/services/foundryAgentService';
import type { FoundryToolDefinition, FoundryAgentVersion } from '@/services/foundryAgentService';
import type { WorkflowDefinition } from '@/types/workflowDefinition';
import { isFoundryAgentAction } from '@/types/workflowDefinition';
import type { Action } from '@/types/workflowDefinition';

// =============================================================================
// FOUNDRY SYNC HELPER
// =============================================================================

/**
 * After saving the workflow, sync Logic Apps tools back to Foundry agents.
 * This ensures Foundry agent definitions stay in sync with Logic Apps extensions.
 * Runs as a fire-and-forget background sync — does not block save.
 */
async function syncFoundryAgentTools(
  workflow: WorkflowDefinition | null,
  agentConnections: Record<string, import('@/types/logicApps').AgentConnection> | undefined,
  getFoundryToken: () => Promise<string | null>,
  addNotification: (notification: { type: NotificationType; title: string; message: string }) => void
): Promise<void> {
  if (!workflow?.definition?.actions) return;

  const actions = workflow.definition.actions;

  for (const [, action] of Object.entries(actions)) {
    if (!isFoundryAgentAction(action as Action)) continue;

    const inputs = (action as Action).inputs;
    if (!inputs || typeof inputs !== 'object' || Array.isArray(inputs)) continue;

    const params = (inputs as Record<string, unknown>).parameters as Record<string, unknown> | undefined;
    if (!params) continue;

    const agentId = params.foundryAgentId as string | undefined;
    if (!agentId) continue;

    const agentVersion = (params.foundryAgentVersion as FoundryAgentVersion | undefined) ?? 'v2';

    // Look up endpoint from agent connection via modelConfigurations reference
    const modelConfigs = (inputs as Record<string, unknown>).modelConfigurations as Record<string, { referenceName?: string }> | undefined;
    const connectionRef = modelConfigs?.model1?.referenceName;
    const projectEndpoint = connectionRef ? agentConnections?.[connectionRef]?.endpoint : undefined;

    if (!projectEndpoint) continue;

    // Collect Logic Apps tools defined on this agent
    const agentAction = action as Action & { tools?: Record<string, unknown> };
    const laTools = agentAction.tools;
    if (!laTools || Object.keys(laTools).length === 0) continue;

    // Convert LA tools to Foundry function tool format
    const foundryTools: FoundryToolDefinition[] = Object.entries(laTools).map(
      ([toolName, tool]) => ({
        type: 'function' as const,
        function: {
          name: toolName,
          description: (tool as Record<string, unknown>)?.description as string | undefined
            ?? `Logic Apps tool: ${toolName}`,
        },
      })
    );

    try {
      const token = await getFoundryToken();
      if (!token) continue;

      await updateFoundryAgentByVersion(projectEndpoint, agentId, { tools: foundryTools }, token, agentVersion);
    } catch (error) {
      console.warn(`[syncFoundryAgentTools] Failed to sync tools for agent ${agentId}:`, error);
      addNotification({
        type: 'warning',
        title: 'Foundry Sync Warning',
        message: `Could not sync tools to Foundry agent. The workflow was saved successfully.`,
      });
    }
  }
}

// =============================================================================
// TYPES
// =============================================================================

export interface UseSaveWorkflowOptions {
  /** Workflow name to save */
  workflowName: string;
  /** Callback on successful save */
  onSuccess?: () => void;
  /** Callback on save error */
  onError?: (error: Error) => void;
  /** Callback when validation fails (save blocked) */
  onValidationFailed?: (result: ValidationResult) => void;
  /** Skip validation before save (not recommended for publish, ok for drafts) */
  skipValidation?: boolean;
  /** Whether to save as draft (uses draft API, skips validation by default) */
  isDraft?: boolean;
}

export interface UseSaveWorkflowResult {
  /** Trigger the save operation */
  save: () => Promise<void>;
  /** Whether a save is in progress */
  isSaving: boolean;
  /** Whether validation is in progress */
  isValidating: boolean;
  /** Whether the workflow has unsaved changes */
  isDirty: boolean;
  /** Last save error, if any */
  error: Error | null;
  /** Last validation result, if any */
  lastValidationResult: ValidationResult | null;
}

/**
 * Hook to save the current workflow to Azure
 */
export function useSaveWorkflow({
  workflowName,
  onSuccess,
  onError,
  onValidationFailed,
  skipValidation = false,
  isDraft: _isDraft = false, // Reserved for future draft-specific save logic
}: UseSaveWorkflowOptions): UseSaveWorkflowResult {
  const queryClient = useQueryClient();
  const { getOttoToken, getFoundryToken } = useAuth();
  const { selectedApp, selectedProject } = useAppStore();
  const addNotification = useNotificationStore((state) => state.addNotification);

  // Validation state
  const [isValidating, setIsValidating] = useState(false);
  const [lastValidationResult, setLastValidationResult] = useState<ValidationResult | null>(null);

  // Timing ref for duration measurement
  const saveStartTimeRef = useRef<number>(0);

  // Get workflow state
  const workflow = useWorkflowStore((state) => state.workflow);
  const isDirty = useWorkflowStore((state) => state.isDirty);
  const markClean = useWorkflowStore((state) => state.markClean);

  // Get connections for saving alongside workflow
  const exportConnections = useConnectionStore((state) => state.exportToConnectionsConfig);

  // Mutation for saving workflow
  const mutation = useMutation({
    mutationFn: async () => {
      saveStartTimeRef.current = performance.now();
      if (!workflow) {
        throw new Error('No workflow to save');
      }

      if (!selectedApp || !selectedProject) {
        throw new Error('No Logic App selected');
      }

      // Build the workflow definition in the expected format
      const workflowDefinition = {
        definition: workflow.definition,
        kind: workflow.kind || 'Stateful',
        ...(workflow.notes && Object.keys(workflow.notes).length > 0 && { notes: workflow.notes }),
      };

      // Get connections config
      const connectionsConfig = exportConnections();

      await saveWorkflow({
        projectName: selectedProject ?? '',
        appName: selectedApp.name ?? '',
        getToken: getOttoToken,
        workflowName,
        workflowDefinition,
        connectionsConfig: connectionsConfig && Object.keys(connectionsConfig).length > 0
          ? connectionsConfig
          : undefined,
      });
    },
    onSuccess: () => {
      const duration_ms = performance.now() - saveStartTimeRef.current;

      // Mark workflow as clean (no unsaved changes)
      markClean();

      // Invalidate workflow queries to refresh data
      queryClient.invalidateQueries({
        queryKey: ['workflowDesigner', selectedProject?.toLowerCase(), selectedApp?.name?.toLowerCase(), workflowName],
      });

      // Refresh trigger metadata (callback URLs etc.) — saving a brand-new
      // HTTP trigger workflow makes its callback URL available, and editing
      // a trigger's name/method may change the URL the runtime returns.
      //
      // The runtime can take a moment to register the URL after save, so we
      // schedule a couple of bounded follow-up invalidations to catch the
      // race instead of waiting for the next manual refresh. The immediate
      // invalidation is broad (so the trigger run dialog also refreshes),
      // but the delayed retries are scoped via predicate to ONLY the
      // per-trigger callback URL queries (`useTriggerCallbackUrl`), to avoid
      // re-running the heavier `useWorkflowTriggers` fan-out (which itself
      // calls /listCallbackUrl once per trigger) on every retry.
      const triggersKey = ['workflowTriggers', workflowName, selectedProject, selectedApp?.name];
      queryClient.invalidateQueries({ queryKey: triggersKey });

      const isCallbackUrlQueryFor = (key: readonly unknown[]): boolean =>
        key.length >= 6 &&
        key[0] === 'workflowTriggers' &&
        key[1] === workflowName &&
        key[2] === selectedProject &&
        key[3] === selectedApp?.name &&
        key[4] === 'callback';

      window.setTimeout(() => {
        queryClient.invalidateQueries({
          predicate: (q) => isCallbackUrlQueryFor(q.queryKey),
        });
      }, 1500);
      window.setTimeout(() => {
        queryClient.invalidateQueries({
          predicate: (q) => isCallbackUrlQueryFor(q.queryKey),
        });
      }, 4000);

      // Sync Logic Apps tools back to Foundry agents
      const agentConns = useConnectionStore.getState().originalConnectionsConfig?.agentConnections;
      syncFoundryAgentTools(workflow, agentConns, getFoundryToken, addNotification);

      // Show success notification
      addNotification({
        type: 'success',
        title: 'Workflow Saved',
        message: `"${workflowName}" has been saved successfully.`,
      });

      telemetryService.trackEvent('workflow.published', undefined, { duration_ms });
      trackFunnelStep(FunnelNames.WORKFLOW_DESIGN, 'publish', 4, { duration_ms });

      onSuccess?.();
    },
    onError: (error: Error) => {
      console.error('[useSaveWorkflow] Save failed:', error);

      // Show error notification
      addNotification({
        type: 'error',
        title: 'Save Failed',
        message: error.message || 'Failed to save workflow',
      });

      telemetryService.trackEvent('workflow.publish_failed', { error: error.message });

      onError?.(error);
    },
  });

  const save = useCallback(async () => {
    if (!isDirty) {
      // Nothing to save
      return;
    }

    if (!workflow || !selectedApp || !selectedProject) {
      return;
    }

    // Step 1: Validate before save (unless skipped)
    if (!skipValidation) {
      setIsValidating(true);
      try {
        const workflowDefinition = {
          definition: workflow.definition,
          kind: workflow.kind || 'Stateful',
        };

        // Validate against the in-memory draft connections so newly-added /
        // unsaved connection references resolve correctly on the backend.
        const draftConnectionsConfig = exportConnections();

        const validationOptions = {
          projectName: selectedProject ?? '',
          appName: selectedApp.name ?? '',
          getToken: getOttoToken,
          workflowName,
          workflowDefinition,
          ...(draftConnectionsConfig && Object.keys(draftConnectionsConfig).length > 0 && {
            connections: draftConnectionsConfig,
          }),
        };

        const validationResult = await validateWorkflowWithAzure(validationOptions);

        setLastValidationResult(validationResult);

        // If validation fails, block save and show errors
        if (!validationResult.isValid) {
          addNotification({
            type: 'error',
            title: 'Validation Failed - Save Blocked',
            message: `Found ${validationResult.validationCount} issue${validationResult.validationCount !== 1 ? 's' : ''} in workflow "${workflowName}". Please fix the errors before saving.`,
            validationErrors: validationResult.errors,
          });
          onValidationFailed?.(validationResult);
          return; // Block save
        }
      } catch (error) {
        console.error('[useSaveWorkflow] Validation failed:', error);
        // On validation error, show warning but allow save
        // This handles network issues, API unavailability, etc.
        addNotification({
          type: 'warning',
          title: 'Validation Skipped',
          message: 'Could not validate workflow. Proceeding with save.',
        });
      } finally {
        setIsValidating(false);
      }
    }

    // Step 2: Save workflow
    await mutation.mutateAsync();
  }, [isDirty, workflow, selectedApp, selectedProject, skipValidation, mutation, exportConnections, getOttoToken, workflowName, addNotification, onValidationFailed]);

  return {
    save,
    isSaving: mutation.isPending,
    isValidating,
    isDirty,
    error: mutation.error,
    lastValidationResult,
  };
}
